import React, { useContext, useEffect, useState } from "react";
import { Alert, Box, Button, TextField } from "@mui/material";
import { Link, useHistory, useLocation } from "react-router-dom";

import { AuthContext } from "../../helpers/AuthContext";
import { loginNetworkPortalMember, setNetworkPortalToken } from "../../services/coreNetworkPortal";
import TalentAuthLayout from "./TalentAuthLayout";
import { resolveTalentReturnTo } from "./talentReturnTo";

const TALENT_LOGIN_IMAGE_URL = "https://images.pexels.com/photos/17771078/pexels-photo-17771078.jpeg";

export default function TalentLogin() {
  const history = useHistory();
  const location = useLocation();
  const returnTo = resolveTalentReturnTo(location.search);
  const { authState, talentAuth } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authState?.status && talentAuth?.member && !talentAuth?.isLoading) {
      history.replace(returnTo);
    }
  }, [authState?.status, history, talentAuth?.isLoading, talentAuth?.member, returnTo]);

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      setLoading(true);
      setError("");
      const response = await loginNetworkPortalMember(email, password);
      if (!response?.accessToken) {
        throw new Error("Login did not return a session.");
      }
      setNetworkPortalToken(response.accessToken);
      history.replace(response.mustChangePassword ? `/talent/change-password?returnTo=${encodeURIComponent(returnTo)}` : returnTo);
    } catch (loginError) {
      setError(loginError?.response?.data?.message || loginError.message || "Unable to log in.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <TalentAuthLayout imageUrl={TALENT_LOGIN_IMAGE_URL} imagePosition="center 42%">
      <Box component="form" onSubmit={handleSubmit}>
        <header>
          <span className="talent-auth-kicker">Talent Portal</span>
          <h1 className="talent-auth-heading">Log In</h1>
          <p className="talent-auth-copy">
            Sign in to review work assigned to your global Talent account across Elite Core instances.
          </p>
        </header>
        {error ? <Alert className="talent-auth-alert" severity="error" role="alert">{error}</Alert> : null}
        <div className="talent-auth-fields">
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
            autoComplete="email"
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            fullWidth
            autoComplete="current-password"
          />
          <Button className="talent-auth-submit" type="submit" variant="contained" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </div>
        <nav className="talent-auth-links" aria-label="Talent account help">
          <Button component={Link} to="/talent/forgot-password" size="small">Forgot password?</Button>
          <Button component={Link} to="/talent/verify-account" size="small">Activate account</Button>
        </nav>
      </Box>
    </TalentAuthLayout>
  );
}
